/**
 * Location parsing utilities for Fusion Manage location strings
 * Handles structured location strings like "GlobalSite - Site - Department"
 */

/**
 * Parse a location string into its components
 * @param {String} locationString - Location string (e.g., "asia - hongkong - controls integration")
 * @returns {Object} - {globalSite, site, department} or null if invalid
 */
export function parseLocationString(locationString) {
  if (!locationString || typeof locationString !== 'string') {
    return null;
  }

  const rawParts = locationString.split(' - ');
  const trimmedParts = rawParts.map(part => {
    if (part === null || part === undefined) {
      return '';
    }
    return String(part).trim();
  });

  const hasAnyContent = trimmedParts.some(part => part.length > 0);
  if (!hasAnyContent) {
    return null;
  }

  const getTrimmedPart = (index) => {
    const value = trimmedParts[index];
    return value && value.length > 0 ? value : null;
  };

  const getRawPart = (index) => {
    const raw = rawParts[index];
    if (raw === null || raw === undefined) {
      return null;
    }
    const str = String(raw);
    return str.length > 0 ? str : null;
  };

  return {
    globalSite: getTrimmedPart(0),
    site: getTrimmedPart(1),
    department: getTrimmedPart(2),
    globalSiteRaw: getRawPart(0),
    siteRaw: getRawPart(1),
    departmentRaw: getRawPart(2),
    fullString: locationString
  };
}

/**
 * Extract unique Global Site values from location items
 * @param {Array} locations - Array of location items with {value, label} structure
 * @returns {Array} - Array of unique {value, label} objects for Global Sites
 */
export function extractUniqueGlobalSites(locations) {
  const globalSites = new Map();

  locations.forEach(location => {
    const source = location.label || location.value || '';
    const parsed = parseLocationString(source);
    if (!parsed || (!parsed.globalSite && !parsed.globalSiteRaw)) {
      return;
    }

    const normalized = (parsed.globalSite || parsed.globalSiteRaw || '').toLowerCase().trim();
    if (!normalized) {
      return;
    }

    if (!globalSites.has(normalized)) {
      const rawValue = parsed.globalSiteRaw ?? parsed.globalSite;
      const displayLabel = (parsed.globalSite ?? '').trim() || (parsed.globalSiteRaw ?? '').trim() || rawValue || '';

      globalSites.set(normalized, {
        value: rawValue ?? displayLabel,
        label: displayLabel || rawValue || '',
        rawValue: rawValue ?? null,
        normalizedValue: (displayLabel || rawValue || ''),
        original: location
      });
    }
  });

  return Array.from(globalSites.values())
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
}

/**
 * Extract unique Site values filtered by Global Site
 * @param {Array} locations - Array of location items
 * @param {String} globalSite - Selected Global Site (optional, case-insensitive)
 * @returns {Array} - Array of unique {value, label} objects for Sites
 */
export function extractUniqueSites(locations, globalSite = null) {
  const sites = new Map();

  const matchesGlobal = (parsed, candidate) => {
    if (!candidate) return true;
    if (!parsed.globalSite && !parsed.globalSiteRaw) return false;
    const parsedValue = (parsed.globalSite || parsed.globalSiteRaw || '').toLowerCase().trim();
    return parsedValue === candidate.toLowerCase().trim();
  };

  locations.forEach(location => {
    const source = location.label || location.value || '';
    const parsed = parseLocationString(source);
    if (!parsed || (!parsed.site && !parsed.siteRaw)) {
      return;
    }

    if (!matchesGlobal(parsed, globalSite)) {
      return;
    }

    const key = (parsed.site || parsed.siteRaw || '').toLowerCase().trim();
    if (!key) {
      return;
    }

    if (!sites.has(key)) {
      const rawValue = parsed.siteRaw ?? parsed.site;
      const displayLabel = (parsed.site ?? '').trim() || (parsed.siteRaw ?? '').trim() || rawValue || '';

      sites.set(key, {
        value: rawValue ?? displayLabel,
        label: displayLabel || rawValue || '',
        rawValue: rawValue ?? null,
        normalizedValue: (displayLabel || rawValue || ''),
        globalSite: parsed.globalSite,
        globalSiteRaw: parsed.globalSiteRaw,
        original: location
      });
    }
  });

  return Array.from(sites.values())
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
}

/**
 * Extract unique Department values filtered by Global Site and Site
 * @param {Array} locations - Array of location items
 * @param {String} globalSite - Selected Global Site (optional, case-insensitive)
 * @param {String} site - Selected Site (optional, case-insensitive)
 * @returns {Array} - Array of unique {value, label} objects for Departments
 */
export function extractUniqueDepartments(locations, globalSite = null, site = null) {
  const departments = new Map();

  const matchesGlobal = (parsed, candidate) => {
    if (!candidate) return true;
    if (!parsed.globalSite && !parsed.globalSiteRaw) return false;
    const parsedValue = (parsed.globalSite || parsed.globalSiteRaw || '').toLowerCase().trim();
    return parsedValue === candidate.toLowerCase().trim();
  };

  const matchesSite = (parsed, candidate) => {
    if (!candidate) return true;
    if (!parsed.site && !parsed.siteRaw) return false;
    const parsedValue = (parsed.site || parsed.siteRaw || '').toLowerCase().trim();
    return parsedValue === candidate.toLowerCase().trim();
  };

  locations.forEach(location => {
    const source = location.label || location.value || '';
    const parsed = parseLocationString(source);
    if (!parsed || (!parsed.department && !parsed.departmentRaw)) {
      return;
    }

    if (!matchesGlobal(parsed, globalSite)) {
      return;
    }

    if (!matchesSite(parsed, site)) {
      return;
    }

    const key = (parsed.department || parsed.departmentRaw || '').toLowerCase().trim();
    if (!key) {
      return;
    }

    if (!departments.has(key)) {
      const rawValue = parsed.departmentRaw ?? parsed.department;
      const displayLabel = (parsed.department ?? '').trim() || (parsed.departmentRaw ?? '').trim() || rawValue || '';

      departments.set(key, {
        value: rawValue ?? displayLabel,
        label: displayLabel || rawValue || '',
        rawValue: rawValue ?? null,
        normalizedValue: (displayLabel || rawValue || ''),
        globalSite: parsed.globalSite,
        globalSiteRaw: parsed.globalSiteRaw,
        site: parsed.site,
        siteRaw: parsed.siteRaw,
        original: location
      });
    }
  });

  return Array.from(departments.values())
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
}

/**
 * Find location items that match a specific Global Site, Site, and Department
 * @param {Array} locations - Array of location items
 * @param {String} globalSite - Global Site to match
 * @param {String} site - Site to match (optional)
 * @param {String} department - Department to match (optional)
 * @returns {Array} - Matching location items
 */
export function findMatchingLocations(locations, globalSite, site = null, department = null) {
  return locations.filter(location => {
    const parsed = parseLocationString(location.label || location.value || '');
    if (!parsed) return false;

    const matchesGlobalSite = parsed.globalSite && 
      parsed.globalSite.toLowerCase().trim() === globalSite.toLowerCase().trim();
    
    const matchesSite = !site || (parsed.site && 
      parsed.site.toLowerCase().trim() === site.toLowerCase().trim());
    
    const matchesDepartment = !department || (parsed.department && 
      parsed.department.toLowerCase().trim() === department.toLowerCase().trim());

    return matchesGlobalSite && matchesSite && matchesDepartment;
  });
}

/**
 * Convert location options to ReactSelect format
 * @param {Array} locations - Array of location objects
 * @returns {Array} - Array formatted for ReactSelect
 */
export function formatForReactSelect(locations) {
  return locations.map(loc => {
    const value = loc.rawValue ?? loc.value ?? loc.label ?? '';
    const label = loc.label || loc.value || value;

    return {
      value,
      label,
      rawValue: loc.rawValue ?? value,
      normalizedValue: loc.normalizedValue ?? label,
      ...loc
    };
  });
}

/**
 * Derive global site from a selected site value
 * Uses the globalSite metadata already included in site objects from extractUniqueSites()
 * @param {Array} sites - Array of site objects with globalSite metadata
 * @param {String} selectedSite - The selected site value
 * @returns {String|null} - The derived global site value or null
 */
export function deriveGlobalSiteFromSite(sites, selectedSite) {
  if (!selectedSite || !Array.isArray(sites)) {
    return null;
  }

  const normalizedSite = String(selectedSite).toLowerCase().trim();
  const matchingSite = sites.find(site => {
    const siteValue = (site.value || site.label || '').toLowerCase().trim();
    return siteValue === normalizedSite;
  });

  return matchingSite?.globalSite || matchingSite?.globalSiteRaw || null;
}

